import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Typography,
  IconButton
} from '@mui/material';
import { RefreshCw, Save, X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../api';
import MethodsList from './MethodsList';
import ObjectsList from './ObjectsList';
import ManualMethodEntry from './ManualMethodEntry';
import ManualObjectEntry from './ManualObjectEntry';

const DEFAULT_FORM = {
  name: '',
  type: 'rest',
  url: '',
  authentication: {
    type: 'none',
    username: '',
    password: '',
    token: '',
    clientId: '',
    clientSecret: ''
  },
  description: '',
  swaggerUrl: '',
  wsdlUrl: '',
  methods: [],
  objects: [],
  saveToDb: true,
  timeout: 30000
};

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ padding: value === index ? '16px 0' : 0 }}>
    {value === index && children}
  </div>
);

const AddEditServiceDialog = ({ open, onClose, onSave, initialData = null }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  useEffect(() => {
    if (open && initialData) {
      //console.log('Initial Data:', initialData); // Ana veriyi logla
      
      try {
        const authConfig = typeof initialData.authenticationConfig === 'string' 
          ? JSON.parse(initialData.authenticationConfig)
          : initialData.authenticationConfig || {};
  
        // Methods işleme
        const processedMethods = (initialData.methods ?? []).map(method => {
          // Parameters işleme
          let parsedParameters = [];
          if (typeof method.parameters === 'string') {
            try {
              parsedParameters = JSON.parse(method.parameters);
            } catch (e) {
              console.error('Error parsing parameters:', e);
              parsedParameters = [];
            }
          } else {
            parsedParameters = Array.isArray(method.parameters) ? method.parameters : [];
          }

          // Responses işleme
          let responses = [];
          if (method.responses) {
            if (typeof method.responses === 'string') {
              try {
                responses = JSON.parse(method.responses);
              } catch (e) {
                console.error('Error parsing responses:', e);
                responses = [{
                  statusCode: "200",
                  description: "Success Response",
                  parameters: []
                }];
              }
            } else {
              responses = Array.isArray(method.responses) ? method.responses : [{
                statusCode: "200",
                description: "Success Response",
                parameters: []
              }];
            }
          } else {
            responses = [{
              statusCode: "200",
              description: "Success Response",
              parameters: []
            }];
          }

          return {
            ...method,
            parameters: parsedParameters,
            responses: responses
          };
        });
        
        //console.log('Processed Methods:', processedMethods); // İşlenmiş methods'u logla
  
        // Objects işleme
        //console.log('Raw Objects:', initialData.objects); // Ham objects verisini logla
  
        const processObjects = (obj) => {
          //console.log('Processing Object:', obj); // Her object'i logla
          
          if (typeof obj === 'string') {
            try {
              const parsed = JSON.parse(obj);
              //console.log('Parsed Object String:', parsed);
              return parsed;
            } catch (e) {
              console.error('Error parsing object string:', e);
              return {};
            }
          }
          return obj;
        };
  
        const processedObjects = (initialData.objects ?? []).map(obj => {
          const processedObj = processObjects(obj);
          let objProperties = [];
  
          //console.log('Object before properties processing:', processedObj); // Properties işlemeden önceki hali
  
          if (processedObj.properties) {
            if (typeof processedObj.properties === 'string') {
              try {
                objProperties = JSON.parse(processedObj.properties);
                console.log('Parsed Properties String:', objProperties);
              } catch (e) {
                console.error('Error parsing properties string:', e);
                objProperties = [];
              }
            } else if (Array.isArray(processedObj.properties)) {
              objProperties = processedObj.properties;
            }
          }
  
          //c//onsole.log('Processed Properties:', objProperties); // İşlenmiş properties'i logla
  
          return {
            name: processedObj.name || '',
            type: processedObj.type || 'object',
            description: processedObj.description || '',
            isRequest: Boolean(processedObj.isRequest),
            isResponse: Boolean(processedObj.isResponse),
            properties: objProperties.map(prop => ({
              name: prop.name || '',
              type: (prop.type || 'string').toLowerCase(),
              description: prop.description || '',
              required: Boolean(prop.required),
              isArray: Boolean(prop.isArray),
              format: prop.format || '',
              enumValues: Array.isArray(prop.enumValues) ? prop.enumValues : []
            }))
          };
        });
  
        //console.log('Final Processed Objects:', processedObjects); // Son işlenmiş objects'i logla
  
        setForm(prevForm => {
          const newForm = {
            name: initialData.name || '',
            type: initialData.type?.toLowerCase() || 'rest',
            url: initialData.url || '',
            authentication: {
              type: initialData.authenticationType?.toLowerCase() || 'none',
              username: authConfig.username || '',
              password: authConfig.password || '',
              token: authConfig.token || '',
              clientId: authConfig.clientId || '',
              clientSecret: authConfig.clientSecret || ''
            },
            description: initialData.description || '',
            swaggerUrl: initialData.swaggerUrl || '',
            wsdlUrl: initialData.wsdlUrl || '',
            methods: processedMethods,
            objects: processedObjects,
            saveToDb: initialData.saveToDb ?? true,
            timeout: initialData.timeout || 30000
          };
          
          //console.log('New Form State:', newForm); // Son form state'ini logla
          return newForm;
        });
  
      } catch (err) {
        console.error('Error in useEffect:', err); // Genel hataları logla
        toast.error('Error loading service data');
      }
    } else if (open) {
      //console.log('Setting Default Form'); // Default form durumunu logla
      setForm(DEFAULT_FORM);
    }
    setCurrentTab(0);
    setError(null);
  }, [open, initialData]);

  const handleInputChange = useCallback((field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleAuthChange = useCallback((field, value) => {
    setForm(prev => ({
      ...prev,
      authentication: {
        ...prev.authentication,
        [field]: value
      }
    }));
  }, []);

  const handleMethodsChange = useCallback((newMethods) => {
    setForm(prev => ({
      ...prev,
      methods: newMethods
    }));
  }, []);
  
  const handleObjectsChange = useCallback((newObjects) => {
    setForm(prev => ({
      ...prev,
      objects: newObjects
    }));
  }, []);

  const handleDiscovery = useCallback(async () => {
    if (!form.url) {
      toast.error('Service URL is required');
      return;
    }
  
    setIsDiscovering(true);
    setError(null);
  
    try {
      console.log("Form state before discovery:", form); // Debug için
  
      const discoveryPayload = {
        servicePreview: {
          name: form.name || 'New Service',
          type: form.type.toLowerCase(), // type kontrolü ekleyelim
          url: form.url.trim(),
          description: form.description || '',
          authenticationType: form.authentication?.type || 'none',
          authenticationConfig: JSON.stringify({
            type: form.authentication?.type || 'none',
            username: form.authentication?.username || '',
            password: form.authentication?.password || '',
            token: form.authentication?.token || '',
            clientId: form.authentication?.clientId || '',
            clientSecret: form.authentication?.clientSecret || ''
          }),
          saveToDb: form.saveToDb ?? true,
          timeout: form.timeout || 30000
        }
      };
  
      // Service type'a göre URL'leri ekleyelim
      if (form.type === 'rest' && form.swaggerUrl) {
        discoveryPayload.servicePreview.swaggerUrl = form.swaggerUrl.trim();
      }
  
      if (form.type === 'soap') {
        discoveryPayload.servicePreview.wsdlUrl = form.wsdlUrl || `${form.url.trim()}?wsdl`;
      }
  
      console.log("Discovery payload:", discoveryPayload); // Debug için
  
      const result = await api.discoverServicePreview(discoveryPayload);
      console.log('Discovery Result:', result); // Debug için
  
      if (!result || result.status === 'Error') {
        throw new Error(result?.message || 'Discovery failed');
      }
  
      const processedData = form.type === 'soap' 
        ? processSoapDiscovery(result)
        : processRestDiscovery(result);
  
      setForm(prev => ({
        ...prev,
        methods: processedData.methods,
        objects: processedData.objects
      }));
  
      setCurrentTab(2);
      toast.success('Service discovery completed');
    } catch (err) {
      console.error('Discovery error:', err); // Debug için detaylı hata
      setError(err.message);
      toast.error(`Discovery failed: ${err.message}`);
    } finally {
      setIsDiscovering(false);
    }
  }, [form]);

  const handleSubmit = useCallback(async () => {
    if (!form.name || !form.url) {
      toast.error('Name and URL are required');
      return;
    }
  
    try {
      // Process methods to ensure they're in the correct format
      const processedMethods = form.methods.map(method => {
        // Ensure responses are properly formatted
        const processedResponses = Array.isArray(method.responses) 
          ? method.responses 
          : typeof method.responses === 'string'
            ? JSON.parse(method.responses)
            : [];
        
        // Ensure parameters are properly formatted
        const processedParameters = Array.isArray(method.parameters)
          ? method.parameters
          : typeof method.parameters === 'string'
            ? JSON.parse(method.parameters)
            : [];
          
        return {
          ...method,
          responses: processedResponses,
          parameters: processedParameters
        };
      });
  
      // Process objects to ensure they're in the correct format
      const processedObjects = form.objects.map(obj => {
        // Ensure properties are properly formatted
        const processedProperties = Array.isArray(obj.properties)
          ? obj.properties
          : typeof obj.properties === 'string'
            ? JSON.parse(obj.properties)
            : [];
          
        return {
          name: obj.name,
          type: obj.type || 'object',
          description: obj.description || '',
          isRequest: Boolean(obj.isRequest),
          isResponse: Boolean(obj.isResponse),
          properties: processedProperties
        };
      });
  
      // Format the service data for API submission
      const serviceData = {
        ...(initialData?.id && { id: initialData.id }),
        name: form.name,
        type: form.type,
        url: form.url,
        swaggerUrl: form.swaggerUrl || '',
        wsdlUrl: form.wsdlUrl || '',
        description: form.description || '',
        authenticationType: form.authentication.type,
        authentication: form.authentication,
        saveToDb: form.saveToDb,
        timeout: form.timeout,
        methods: processedMethods,
        objects: processedObjects
      };
  
      console.log('Submitting service data:', JSON.stringify(serviceData, null, 2));
  
      if (initialData?.id) {
        await api.updateService(initialData.id, serviceData);
        toast.success('Service updated successfully');
      } else {
        await api.createService(serviceData);
        toast.success('Service created successfully');
      }
  
      onClose();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(`Failed to save: ${err.message}`);
    }
  }, [form, initialData, onClose]);
  
  const normalizeType = (type) => {
    if (!type || typeof type !== 'string') return 'string';
    
    const typeStr = type.toLowerCase().trim();
    
    // Array tiplerini kontrol et
    if (typeStr.startsWith('array<')) {
      return 'array';
    }
    
    // Temel tip eşleştirmeleri
    const typeMap = {
      'int': 'integer',
      'int32': 'integer',
      'int64': 'integer',
      'integer': 'integer',
      'long': 'integer',
      'float': 'number',
      'double': 'number',
      'decimal': 'number',
      'bool': 'boolean',
      'boolean': 'boolean',
      'string': 'string',
      'date': 'string',
      'datetime': 'string',
      'binary': 'string',
      'byte': 'string',
      'array': 'array',
      'object': 'object',
      'file': 'file'
    };
  
    return typeMap[typeStr] || 'string';
  };

  const convertProperties = (propertiesObj) => {
    if (!propertiesObj) return [];
    
    return Object.entries(propertiesObj).map(([name, prop]) => ({
      name: name,
      type: normalizeType(prop.Type || prop.type),
      description: prop.Description || prop.description || '',
      required: Boolean(prop.Required || prop.required),
      isArray: Boolean(prop.Type?.startsWith('array') || prop.type?.startsWith('array')),
      format: prop.Format || prop.format || '',
      enumValues: prop.EnumValues || prop.enumValues || [],
      objectRef: prop.ObjectRef || prop.objectRef || ''
    }));
  };

  const processSoapDiscovery = (result) => {
    console.log('Processing SOAP Discovery Result:', result);
    
    const methods = ensureArray(result.methods).map(method => {
      // Request parametrelerini işle
      const processedParams = ensureArray(method.parameters).map(param => {
        const actualType = param.objectRef ? 'object' : normalizeType(param.type);
        
        return {
          name: param.name || '',
          type: actualType,
          description: param.description || '',
          required: Boolean(param.required),
          isArray: Boolean(param.isArray || param.type === 'array'),
          format: param.format || '',
          enumValues: ensureArray(param.enumValues),
          objectRef: param.objectRef || '',
          // SOAP için özel alanlar
          namespace: param.namespace || method.namespace || '',
          elementName: param.elementName || param.name || '',
          properties: processProperties(param.properties || [])
        };
      });
  
      // Response'ları işle
      const processedResponses = [{
        statusCode: "200",
        description: "Success Response",
        type: method.returnType || 'object',
        objectRef: method.returnObjectRef || '',
        isArray: Boolean(method.isArrayReturn),
        parameters: processParameters(method.responses?.[0]?.parameters || []),
        // SOAP için özel alanlar
        faultActor: method.responses?.[0]?.faultActor || null,
        faultString: method.responses?.[0]?.faultString || null,
        faultCode: method.responses?.[0]?.faultCode || null
      }];
  
      // Hata response'larını ekle
      if (method.responses?.length > 1) {
        method.responses.slice(1).forEach(errResponse => {
          if (errResponse) {
            processedResponses.push({
              statusCode: errResponse.statusCode || "500",
              description: errResponse.description || "SOAP Fault",
              type: errResponse.type || 'object',
              objectRef: errResponse.objectRef || '',
              isArray: Boolean(errResponse.isArray),
              parameters: processParameters(errResponse.parameters || []),
              faultActor: errResponse.faultActor || null,
              faultString: errResponse.faultString || null,
              faultCode: errResponse.faultCode || null
            });
          }
        });
      }
  
      return {
        name: method.name || '',
        description: method.description || `Operation ${method.name}`,
        type: 'soap',
        soapAction: method.soapAction || `http://tempuri.org/${method.name}`,
        namespace: method.namespace || 'http://tempuri.org/',
        parameters: processedParams,
        responses: processedResponses,
        returnType: method.returnType || 'void',
        returnObjectRef: method.returnObjectRef || '',
        isArrayReturn: Boolean(method.isArrayReturn),
        // SOAP için özel alanlar
        operationName: method.operationName || method.name || '',
        requestName: `${method.name}Request`,
        responseName: `${method.name}Response`
      };
    });
  
    // API'den gelen objects listesini işle
    const objects = ensureArray(result.objects).map(obj => {
      // Properties kısmı string ise parse et
      let properties = [];
      if (typeof obj.properties === 'string') {
        try {
          properties = convertProperties(JSON.parse(obj.properties));
        } catch (e) {
          console.error('Error parsing SOAP object properties string:', e);
          properties = [];
        }
      } else if (typeof obj.properties === 'object') {
        properties = convertProperties(obj.properties);
      }
  
      return {
        name: obj.name,
        type: obj.type || 'object',
        description: obj.description || '',
        isRequest: Boolean(obj.isRequest),
        isResponse: Boolean(obj.isResponse),
        properties: properties.map(prop => ({
          ...prop,
          // SOAP için özel alanlar
          namespace: prop.namespace || obj.namespace || '',
          nillable: Boolean(prop.nillable),
          minOccurs: prop.minOccurs || 0,
          maxOccurs: prop.maxOccurs || 1
        }))
      };
    });
  
    // SOAP servisi için otomatik request/response objelerini oluştur
    methods.forEach(method => {
      // Request objesi
      if (method.parameters.length > 0) {
        const requestObj = {
          name: `${method.name}Request`,
          type: 'object',
          description: `Request type for ${method.name} operation`,
          isRequest: true,
          isResponse: false,
          properties: method.parameters.map(param => ({
            name: param.name,
            type: param.type,
            description: param.description,
            required: param.required,
            isArray: param.isArray,
            format: param.format,
            enumValues: param.enumValues,
            objectRef: param.objectRef,
            namespace: param.namespace,
            nillable: param.nillable || !param.required,
            minOccurs: param.required ? 1 : 0,
            maxOccurs: param.isArray ? 'unbounded' : 1
          }))
        };
        
        if (!objects.some(obj => obj.name === requestObj.name)) {
          objects.push(requestObj);
        }
      }
  
      // Response objesi
      if (method.returnType !== 'void') {
        const responseObj = {
          name: `${method.name}Response`,
          type: 'object',
          description: `Response type for ${method.name} operation`,
          isRequest: false,
          isResponse: true,
          properties: [{
            name: 'return',
            type: method.returnType,
            description: 'Operation return value',
            required: true,
            isArray: method.isArrayReturn,
            objectRef: method.returnObjectRef,
            namespace: method.namespace,
            nillable: false,
            minOccurs: 1,
            maxOccurs: method.isArrayReturn ? 'unbounded' : 1
          }]
        };
        
        if (!objects.some(obj => obj.name === responseObj.name)) {
          objects.push(responseObj);
        }
      }
    });
  
    return { methods, objects };
  };
  
  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

const processRestDiscovery = (result) => {
  console.log('Processing REST Discovery Result:', result);
  const methods = ensureArray(result.methods).map(method => {
    // Metodları işle
    const processedParams = processParameters(method?.parameters || []);
    const processedResponses = ensureArray(method.responses).map(response => ({
      statusCode: (response?.statusCode || '200').toString(),
      description: response?.description || 'Success Response',
      type: response?.type || 'object',
      objectRef: response?.objectRef || '',
      isArray: Boolean(response?.isArray),
      parameters: processParameters(response?.parameters || [])
    }));

    return {
      name: method?.name || '',
      description: method?.description || '',
      type: 'rest',
      httpMethod: method?.httpMethod || 'GET',
      path: method?.path || '/',
      parameters: processedParams,
      responses: processedResponses,
      returnType: method?.returnType || 'void',
      returnObjectRef: method?.returnObjectRef || '',
      isArrayReturn: Boolean(method?.isArrayReturn)
    };
  });

  // API'den gelen objects listesini işle
  const objects = ensureArray(result.objects).map(obj => {
    // Properties kısmı string ise parse et
    let properties = [];
    if (typeof obj.properties === 'string') {
      try {
        properties = convertProperties(JSON.parse(obj.properties));
      } catch (e) {
        console.error('Error parsing properties string:', e);
        properties = [];
      }
    } else if (typeof obj.properties === 'object') {
      // Properties direkt obje olarak geldiyse dönüştür
      properties = convertProperties(obj.properties);
    }

    return {
      name: obj.name,
      type: obj.type || 'object',
      description: obj.description || '',
      isRequest: Boolean(obj.isRequest),
      isResponse: Boolean(obj.isResponse),
      properties: properties
    };
  });

  return { methods, objects };
};

  const ensureArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const processParameters = (params) => {
    return ensureArray(params).map(param => {
      // objectRef varsa, bunu type'dan öncelikli kullan
      const actualType = param.objectRef ? 'object' : normalizeType(param.type);
      
      return {
        name: param.name || '',
        type: actualType,
        description: param.description || '',
        required: Boolean(param.required),
        isArray: Boolean(param.isArray || param.type === 'array'),
        format: param.format || '',
        enumValues: ensureArray(param.enumValues),
        objectRef: param.objectRef || '',
        properties: processProperties(param.properties || [])
      };
    });
  };
  
  const processProperties = (props) => {
    return ensureArray(props).map(prop => {
      const actualType = prop.objectRef ? 'object' : normalizeType(prop.type);
      
      return {
        name: prop.name || prop.Name || '',
        type: actualType,
        description: prop.description || prop.Description || '',
        required: Boolean(prop.required || prop.Required),
        isArray: Boolean(prop.isArray || prop.IsArray || prop.type === 'array' || prop.Type === 'array'),
        format: prop.format || prop.Format || '',
        enumValues: ensureArray(prop.enumValues || prop.EnumValues),
        objectRef: prop.objectRef || prop.ObjectRef || ''
      };
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#f8f4ff',
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          bgcolor: '#edbc45',
          color: '#000',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600
        }}
      >
        {initialData ? `Edit Service: ${initialData.name}` : 'Add New Service'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: '#000',
            p: 0.5,
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Tabs
          value={currentTab}
          onChange={(e, val) => setCurrentTab(val)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            mb: 2,
            '& .MuiTab-root': {
              color: '#000',
              '&.Mui-selected': {
                color: '#edbc45',
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#edbc45'
            }
          }}
        >
          <Tab label="BASIC INFO" />
          <Tab label="AUTHENTICATION" />
          <Tab label="METHODS" />
          <Tab label="OBJECTS" />
          <Tab label="ADVANCED" />
        </Tabs>

        {/* Basic Info Tab */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Service Name"
                value={form.name}
                onChange={e => handleInputChange('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Service Type</InputLabel>
                <Select
                  value={form.type}
                  onChange={e => handleInputChange('type', e.target.value)}
                  label="Service Type"
                >
                  <MenuItem value="rest">REST/JSON</MenuItem>
                  <MenuItem value="soap">SOAP/XML</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Service URL"
                value={form.url}
                onChange={e => handleInputChange('url', e.target.value)}
              />
            </Grid>
            {form.type === 'rest' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Swagger/OpenAPI URL"
                  value={form.swaggerUrl}
                  onChange={e => handleInputChange('swaggerUrl', e.target.value)}
                  helperText="Optional: Provide Swagger/OpenAPI documentation URL"
                />
              </Grid>
            )}
            {form.type === 'soap' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="WSDL URL"
                  value={form.wsdlUrl}
                  onChange={e => handleInputChange('wsdlUrl', e.target.value)}
                  helperText="Optional: Provide WSDL URL"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshCw />}
                  onClick={handleDiscovery}
                  disabled={isDiscovering}
                  sx={{
                    backgroundColor: '#edbc45',
                    color: '#000',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#d4a63a'
                    },
                    '&:disabled': {
                      backgroundColor: '#f0f0f0',
                      color: '#999'
                    }
                  }}
                >
                  {isDiscovering ? 'Discovering...' : 'Discover Service'}
                </Button>
                {isDiscovering && <CircularProgress size={24} sx={{ color: '#edbc45' }} />}
              </Box>
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Authentication Tab */}
        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Authentication Type</InputLabel>
                <Select
                  value={form.authentication.type}
                  onChange={e => handleAuthChange('type', e.target.value)}
                  label="Authentication Type"
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="basic">Basic Auth</MenuItem>
                  <MenuItem value="bearer">Bearer Token</MenuItem>
                  <MenuItem value="oauth2">OAuth 2.0</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {form.authentication.type === 'basic' && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={form.authentication.username}
                    onChange={e => handleAuthChange('username', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    value={form.authentication.password}
                    onChange={e => handleAuthChange('password', e.target.value)}
                  />
                </Grid>
              </>
            )}
            
            {form.authentication.type === 'bearer' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token"
                  value={form.authentication.token}
                  onChange={e => handleAuthChange('token', e.target.value)}
                />
              </Grid>
            )}

            {form.authentication.type === 'oauth2' && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Client ID"
                    value={form.authentication.clientId}
                    onChange={e => handleAuthChange('clientId', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Client Secret"
                    value={form.authentication.clientSecret}
                    onChange={e => handleAuthChange('clientSecret', e.target.value)}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        {/* Methods Tab */}
        <TabPanel value={currentTab} index={2}>
          <MethodsList methods={form.methods} serviceType={form.type}  onMethodsChange={handleMethodsChange}/>
        </TabPanel>

        {/* Objects Tab */}
        <TabPanel value={currentTab} index={3}>
          <ObjectsList objects={form.objects} serviceType={form.type} onObjectsChange={handleObjectsChange}/>
        </TabPanel>

{/* Advanced Tab */}
<TabPanel value={currentTab} index={4}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.saveToDb}
                    onChange={e => handleInputChange('saveToDb', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#edbc45',
                        '&:hover': {
                          backgroundColor: 'rgba(237, 188, 69, 0.08)',
                        },
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#edbc45',
                      },
                    }}
                  />
                }
                label="Save Response to Database"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Timeout (ms)"
                value={form.timeout}
                onChange={e => handleInputChange('timeout', parseInt(e.target.value))}
                helperText="Request timeout in milliseconds"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={form.description}
                onChange={e => handleInputChange('description', e.target.value)}
              />
            </Grid>
          </Grid>
        </TabPanel>

      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ color: '#000', borderColor: '#000' }}
        >
          CANCEL
        </Button>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit}
          sx={{
            backgroundColor: '#edbc45',
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#d4a63a'
            }
          }}
        >
          SAVE SERVICE
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditServiceDialog;